/**
 * MarsLivePage -- top-level `/mars-live` page.
 *
 * Section order:
 *   1. Compact header row: page title + "As of" stamp + fund selector pills
 *   2. Vintage slider
 *   3. 3x2 fan chart grid
 *   4. "Portfolio" controls row: asset selector + Period pills + toggles
 *   5. 3-column grid: Expected Returns + Asset Class Volatilities +
 *      Portfolio Exposures
 *
 * Spacing is deliberately tight — the goal is the whole page on one
 * laptop screen (the shared PageHeader is skipped for a slim local
 * header for the same reason).
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Loader from '@/components/Loader';
import { useAuth } from '@/lib/auth/useAuth';
import { formatLong } from '@/lib/utils/dates';
import { fetchVintageList, mlKeys } from './api';
import { useMarsLiveStore, type LiveFundId, type Timeframe } from './store';
import VintageSlider from './components/VintageSlider';
import FanPlotsGrid from './components/FanPlotsGrid';
import ExpectedReturnsChart from './components/ExpectedReturnsChart';
import VolatilityChart from './components/VolatilityChart';
import PortfolioExposuresChart from './components/PortfolioExposuresChart';
import type { UiAsset } from './types';

const FUND_OPTIONS: ReadonlyArray<{ value: LiveFundId; label: string }> = [
  { value: 'mars-fqm', label: 'FQM Alpha' },
  { value: 'mars-dar-nn', label: 'DAR NN' },
  { value: 'mars-fidelity-balanced', label: 'Balanced' },
  { value: 'mars-fidelity-conservative', label: 'Conservative' },
];

const TIMEFRAMES: ReadonlyArray<{ value: Timeframe; label: string }> = [
  { value: '0.5y', label: '6M' },
  { value: '1y', label: '1Y' },
  { value: '3y', label: '3Y' },
];

const ASSETS: ReadonlyArray<{ value: UiAsset; label: string; color: string }> = [
  { value: 'Equities', label: 'Equities', color: '#ec665c' },
  { value: 'Bonds', label: 'Fixed Income', color: '#729dcd' },
  { value: 'Commodities', label: 'Commodities', color: '#ff9d4a' },
  { value: 'Inflation', label: 'Inflation Swaps', color: '#9467bd' },
];

export default function MarsLivePage(): JSX.Element {
  const {
    data: vintages,
    isLoading,
    error,
  } = useQuery({
    queryKey: mlKeys.vintagesList(),
    queryFn: fetchVintageList,
    staleTime: 5 * 60 * 1000,
  });

  const selectedVintage = useMarsLiveStore((s) => s.selectedVintage);
  const setSelectedVintage = useMarsLiveStore((s) => s.setSelectedVintage);
  const selectedAssets = useMarsLiveStore((s) => s.selectedAssets);
  const toggleAsset = useMarsLiveStore((s) => s.toggleAsset);
  const timeframe = useMarsLiveStore((s) => s.timeframe);
  const setTimeframe = useMarsLiveStore((s) => s.setTimeframe);
  const fundId = useMarsLiveStore((s) => s.fundId);
  const setFundId = useMarsLiveStore((s) => s.setFundId);
  const volTiming = useMarsLiveStore((s) => s.volTiming);
  const setVolTiming = useMarsLiveStore((s) => s.setVolTiming);
  const transactionCosts = useMarsLiveStore((s) => s.transactionCosts);
  const setTransactionCosts = useMarsLiveStore((s) => s.setTransactionCosts);
  const constraints = useMarsLiveStore((s) => s.constraints);
  const setConstraints = useMarsLiveStore((s) => s.setConstraints);

  useEffect(() => {
    if (!vintages?.length) return;
    if (!selectedVintage || !vintages.includes(selectedVintage)) {
      setSelectedVintage(vintages[vintages.length - 1]);
    }
  }, [vintages, selectedVintage, setSelectedVintage]);

  // Fund pills come from the backend's fund allowlist (/auth/status →
  // portfolio_funds) — the single source of truth. A fulcrum PM sees only
  // DAR NN; superusers see everything. Never re-derive this from the org.
  const { portfolioFunds } = useAuth();
  const fundOptions = useMemo(
    () => FUND_OPTIONS.filter((f) => portfolioFunds.includes(f.value)),
    [portfolioFunds],
  );

  // Snap the selected fund to one the user can actually access (the store
  // default is FQM, which a PM cannot fetch).
  useEffect(() => {
    if (!fundOptions.length) return;
    if (!fundOptions.some((f) => f.value === fundId)) {
      setFundId(fundOptions[0].value);
    }
  }, [fundOptions, fundId, setFundId]);

  if (isLoading) {
    return (
      <div className="p-8">
        <Loader label="Loading" />
      </div>
    );
  }
  if (error || !vintages?.length) {
    return <div className="p-8 text-sm text-red-600">Failed to load MARS vintage list.</div>;
  }

  const currentVintage = selectedVintage ?? vintages[vintages.length - 1];

  return (
    // 1200px, not wider: stretching the grid flattens the fan charts'
    // aspect ratio and the actual-series wiggles become unreadable.
    <div className="mx-auto max-w-[1200px] px-6 pb-4 pt-2">
      {/* Header + Fund pills */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-baseline gap-3">
          <h2 className="font-[var(--font-heading)] text-xl font-bold text-[color:var(--text-dark)] m-0">
            MARS Live
          </h2>
          <span className="text-[0.85rem] text-gray-500">As of {formatLong(new Date())}</span>
        </div>
        <div className="flex border border-gray-300 rounded-lg overflow-hidden shadow-sm">
          {fundOptions.map((f, i) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFundId(f.value)}
              className={[
                'px-3 py-1.5 text-sm font-medium transition-all duration-200',
                'font-[var(--font-body)]',
                fundId === f.value
                  ? 'bg-[color:var(--accent)] text-white font-semibold shadow-[inset_0_1px_3px_rgba(0,0,0,0.1)]'
                  : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800',
                i < fundOptions.length - 1 ? 'border-r border-gray-300' : '',
              ].join(' ')}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <VintageSlider vintages={vintages} value={currentVintage} onChange={setSelectedVintage} />

      <FanPlotsGrid vintageDate={currentVintage} vintages={vintages} />

      {/* Portfolio controls row: heading + asset selector + period + toggles.
       * One wrapping row instead of two stacked ones to save vertical space. */}
      <div className="flex items-center justify-between flex-wrap gap-x-6 gap-y-2 mt-3 mb-2">
        <div className="flex items-center gap-4 flex-wrap">
          <h3 className="text-[0.95rem] font-semibold text-gray-700 m-0">Portfolio</h3>
          <div className="flex gap-[0.4rem] flex-wrap">
            {ASSETS.map((a) => {
              const selected = selectedAssets.has(a.value);
              return (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => toggleAsset(a.value)}
                  aria-pressed={selected}
                  className={[
                    'flex items-center justify-between gap-[0.375rem]',
                    'px-2 py-[0.375rem] rounded text-xs font-medium text-gray-700',
                    'border transition-all cursor-pointer',
                    selected
                      ? 'bg-[color:var(--accent-light)] border-[color:var(--accent)] shadow-[0_1px_4px_rgba(78,183,172,0.15)]'
                      : 'bg-white border-gray-200 hover:bg-[color:var(--accent-light)] hover:border-[color:var(--accent)]',
                  ].join(' ')}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-block w-3 h-3 rounded-[2px] shrink-0"
                      style={{ background: a.color, opacity: selected ? 1 : 0.4 }}
                    />
                    <span className="leading-[1.2]">{a.label}</span>
                  </span>
                  <span className="text-[0.8rem] w-[14px] text-center">{selected ? '✓' : '+'}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-700 text-sm">Period:</span>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden shadow-sm">
              {TIMEFRAMES.map((tf, i) => (
                <button
                  key={tf.value}
                  type="button"
                  onClick={() => setTimeframe(tf.value)}
                  className={[
                    'px-3 py-1.5 text-sm font-medium transition-all duration-200',
                    'font-[var(--font-body)]',
                    timeframe === tf.value
                      ? 'bg-[color:var(--accent)] text-white font-semibold shadow-[inset_0_1px_3px_rgba(0,0,0,0.1)]'
                      : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800',
                    i < TIMEFRAMES.length - 1 ? 'border-r border-gray-300' : '',
                  ].join(' ')}
                >
                  {tf.label}
                </button>
              ))}
            </div>
          </div>

          {/* No "Exposures:" label — the toggle names explain themselves and
           * the row must fit on one line at the 1200px container width. */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={volTiming}
                  onChange={(e) => setVolTiming(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#4EB7AC]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#4EB7AC]" />
              </div>
              <span className="text-sm text-gray-700">Vol Timing</span>
            </label>
            {fundId === 'mars-dar-nn' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={constraints}
                    onChange={(e) => setConstraints(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#4EB7AC]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#4EB7AC]" />
                </div>
                <span className="text-sm text-gray-700">Constraints</span>
              </label>
            )}
            <label
              className={[
                'flex items-center gap-2',
                volTiming ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
              ].join(' ')}
              title={volTiming ? undefined : 'Vol Timing must be ON to use Transaction Costs'}
            >
              <div className="relative">
                <input
                  type="checkbox"
                  checked={transactionCosts}
                  disabled={!volTiming}
                  onChange={(e) => setTransactionCosts(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#4EB7AC]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#4EB7AC] peer-disabled:cursor-not-allowed" />
              </div>
              <span className="text-sm text-gray-700">Transaction Costs</span>
            </label>
          </div>
        </div>
      </div>

      {/* Portfolio charts: Expected Returns + Volatilities + Exposures in
       * one row so the page fits a laptop screen (stacks below lg). */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <ExpectedReturnsChart
          selectedAssets={selectedAssets}
          vintageDate={currentVintage}
          timeframe={timeframe}
        />
        <VolatilityChart
          selectedAssets={selectedAssets}
          vintageDate={currentVintage}
          timeframe={timeframe}
        />
        <PortfolioExposuresChart
          selectedAssets={selectedAssets}
          vintageDate={currentVintage}
          timeframe={timeframe}
          fundId={fundId}
          volTiming={volTiming}
          transactionCosts={transactionCosts}
          constraints={constraints}
        />
      </div>

      {/* Second slider under the charts so the vintage can be scrubbed
       * without scrolling back up. keyboardNav off: the top instance owns
       * the arrow keys (both would double-step otherwise). */}
      <div className="mt-2">
        <VintageSlider
          vintages={vintages}
          value={currentVintage}
          onChange={setSelectedVintage}
          keyboardNav={false}
        />
      </div>
    </div>
  );
}
